import { useState } from "react";
import { Plus, Eye, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import type { DoctorListItem, LabTestOption, MedicineEntry } from "../../services/api/admin-order.types";
import DoctorSelectModal from "./DoctorSelectModal";
import LabTestModal from "./LabTestModal";
import MedicineModal from "./MedicineModal";
import PrescriptionPreview from "./PrescriptionPreview";

interface ConsultationFormProps {
  onGeneratePrescription: (data: {
    doctor: DoctorListItem;
    chiefComplaints: string;
    diagnosis: string;
    labTests: LabTestOption[];
    medicines: MedicineEntry[];
    specialInstructions: string;
  }) => void;
  generating?: boolean;
}

const HARDCODED_DOCTOR: DoctorListItem = {
  id: "cm82og5mg00b5hi01nqql7oum",
  name: "Dr Piyush Kumar",
  mobileNumber: "7991149424",
  registrationNumber: "R/21302 (DMC)",
  hospitalName: "Dandar Health",
  doctorEducation: "MBBS,MD",
  doctorSpecialization: "General Physician",
  address: " Harmu Housing Colony, Ranchi, Jharkhand, 834001  ",
};

const ConsultationForm: React.FC<ConsultationFormProps> = ({ onGeneratePrescription, generating }) => {
  const [doctor, setDoctor] = useState<DoctorListItem | null>(null);
  const [chiefComplaints, setChiefComplaints] = useState("");
  const [diagnosis, setDiagnosis] = useState("");
  const [labTests, setLabTests] = useState<LabTestOption[]>([]);
  const [medicines, setMedicines] = useState<MedicineEntry[]>([]);
  const [specialInstructions, setSpecialInstructions] = useState("");

  const [showDoctorModal, setShowDoctorModal] = useState(false);
  const [showLabTestModal, setShowLabTestModal] = useState(false);
  const [showMedicineModal, setShowMedicineModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const canGenerate = doctor && chiefComplaints.trim();

  const handleGenerate = () => {
    if (!doctor) {
      toast.error("Please select a doctor");
      return;
    }
    if (!chiefComplaints.trim()) {
      toast.error("Please enter chief complaints");
      return;
    }
    onGeneratePrescription({ doctor, chiefComplaints, diagnosis, labTests, medicines, specialInstructions });
  };

  return (
    <div>
      {/* Header with Preview */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-slate-800">Consultation Details</h3>
        <button
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-emerald-300 text-emerald-600 bg-white hover:bg-emerald-50 transition-colors cursor-pointer disabled:opacity-40"
          disabled={!canGenerate}
          onClick={() => setShowPreview(true)}
        >
          <Eye size={13} /> Preview
        </button>
      </div>

      {/* Doctor Details */}
      <div className="mb-5">
        <label className="text-[0.65rem] font-bold text-slate-500 uppercase tracking-wider block mb-2">Doctor Details</label>
        {doctor ? (
          <div className="flex items-center justify-between p-3 rounded-xl border border-slate-200 bg-slate-50">
            <div>
              <div className="text-xs font-bold text-slate-800">{doctor.name}</div>
              <div className="text-[0.6rem] text-slate-400">
                {doctor.doctorEducation} · {doctor.registrationNumber}
              </div>
              <div className="text-[0.6rem] text-slate-400">{doctor.hospitalName}</div>
            </div>
            <button
              className="text-xs text-indigo-500 font-semibold bg-transparent border-none cursor-pointer hover:text-indigo-700"
              onClick={() => setShowDoctorModal(true)}
            >
              Change
            </button>
          </div>
        ) : (
          <button
            className="w-full py-3 rounded-xl border-2 border-dashed border-slate-300 text-xs font-semibold text-emerald-600 hover:border-emerald-400 hover:bg-emerald-50/50 transition-colors cursor-pointer bg-white flex items-center justify-center gap-1.5"
            onClick={() => setShowDoctorModal(true)}
          >
            <Plus size={14} /> Select Doctor
          </button>
        )}
      </div>

      {/* Chief Complaints */}
      <div className="mb-5">
        <label className="text-[0.65rem] font-bold text-slate-500 uppercase tracking-wider block mb-2">Chief Complaints</label>
        <textarea
          value={chiefComplaints}
          onChange={(e) => setChiefComplaints(e.target.value)}
          placeholder="Write your diagnosis here..."
          className="w-full px-3 py-2.5 text-xs border border-slate-200 rounded-xl outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 resize-none h-20 transition-all"
        />
      </div>

      {/* Diagnosis */}
      <div className="mb-5">
        <label className="text-[0.65rem] font-bold text-slate-500 uppercase tracking-wider block mb-2">Diagnosis</label>
        <textarea
          value={diagnosis}
          onChange={(e) => setDiagnosis(e.target.value)}
          placeholder="Write your diagnosis here..."
          className="w-full px-3 py-2.5 text-xs border border-slate-200 rounded-xl outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 resize-none h-20 transition-all"
        />
      </div>

      {/* Lab Tests */}
      <div className="mb-5">
        <label className="text-[0.65rem] font-bold text-slate-500 uppercase tracking-wider block mb-2">Lab Tests</label>
        {labTests.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {labTests.map((test) => (
              <div key={test.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 text-xs font-medium border border-indigo-200">
                {test.name}
                <button
                  className="w-4 h-4 rounded-full bg-indigo-200 text-indigo-600 flex items-center justify-center hover:bg-red-200 hover:text-red-600 transition-colors border-none cursor-pointer"
                  onClick={() => setLabTests((prev) => prev.filter((t) => t.id !== test.id))}
                >
                  <Trash2 size={9} />
                </button>
              </div>
            ))}
          </div>
        )}
        <button
          className="w-full py-3 rounded-xl border-2 border-dashed border-slate-300 text-xs font-semibold text-emerald-600 hover:border-emerald-400 hover:bg-emerald-50/50 transition-colors cursor-pointer bg-white flex items-center justify-center gap-1.5"
          onClick={() => setShowLabTestModal(true)}
        >
          <Plus size={14} /> Add Tests
        </button>
      </div>

      {/* Medicines */}
      <div className="mb-5">
        <label className="text-[0.65rem] font-bold text-slate-500 uppercase tracking-wider block mb-2">Medicines</label>
        {medicines.length > 0 && (
          <div className="space-y-2 mb-2">
            {medicines.map((med, i) => (
              <div key={`${med.medicineId}-${i}`} className="flex items-center justify-between px-3 py-2 rounded-lg border border-slate-200 bg-slate-50">
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-slate-800 truncate">{med.medicineName}</div>
                  <div className="text-[0.6rem] text-slate-400 truncate">{med.generatedText || med.originalText}</div>
                </div>
                <button
                  className="w-6 h-6 rounded-full bg-red-100 text-red-500 flex items-center justify-center hover:bg-red-200 transition-colors border-none cursor-pointer ml-2 flex-shrink-0"
                  onClick={() => setMedicines((prev) => prev.filter((_, idx) => idx !== i))}
                >
                  <Trash2 size={10} />
                </button>
              </div>
            ))}
          </div>
        )}
        <button
          className="w-full py-3 rounded-xl border-2 border-dashed border-slate-300 text-xs font-semibold text-emerald-600 hover:border-emerald-400 hover:bg-emerald-50/50 transition-colors cursor-pointer bg-white flex items-center justify-center gap-1.5"
          onClick={() => setShowMedicineModal(true)}
        >
          <Plus size={14} /> Add Medicines
        </button>
      </div>

      {/* Special Instructions */}
      <div className="mb-5">
        <label className="text-[0.65rem] font-bold text-slate-500 uppercase tracking-wider block mb-2">Special Instructions (If Any)</label>
        <textarea
          value={specialInstructions}
          onChange={(e) => setSpecialInstructions(e.target.value)}
          placeholder="Write your note..."
          className="w-full px-3 py-2.5 text-xs border border-slate-200 rounded-xl outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 resize-none h-20 transition-all"
        />
      </div>

      {/* Modals */}
      {showDoctorModal && (
        <DoctorSelectModal
          onClose={() => setShowDoctorModal(false)}
          onSelect={(d) => { setDoctor(d); setShowDoctorModal(false); }}
          doctors={[HARDCODED_DOCTOR]}
        />
      )}
      {showLabTestModal && (
        <LabTestModal
          onClose={() => setShowLabTestModal(false)}
          onSave={(tests) => { setLabTests(tests); setShowLabTestModal(false); }}
          initialTests={labTests}
        />
      )}
      {showMedicineModal && (
        <MedicineModal
          onClose={() => setShowMedicineModal(false)}
          onSave={(meds) => { setMedicines(meds); setShowMedicineModal(false); }}
          initialMedicines={medicines}
        />
      )}
      {showPreview && (
        <PrescriptionPreview
          onClose={() => setShowPreview(false)}
          doctor={doctor}
          chiefComplaints={chiefComplaints}
          diagnosis={diagnosis}
          labTests={labTests}
          medicines={medicines}
          specialInstructions={specialInstructions}
        />
      )}
      <button
        data-generate-trigger
        className="hidden"
        onClick={handleGenerate}
      />
    </div>
  );
};

export default ConsultationForm;
